import React, { useState } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import NotificationDock from '../notifications/NotificationDock';

import { useAuth } from '../../context/AuthContext';
import AppSidebar from '../layout/AppSidebar';
import OverviewPanel from './components/OverviewPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import PolicyDesk from '../government/components/PolicyDesk';
import styles from '../enterprise/pages/EnterpriseDashboardPage.module.css';

type DashboardVariant = 'startup' | 'government';

interface SegmentDashboardShellProps {
  variant: DashboardVariant;
}

const DASHBOARD_CONFIG: Record<DashboardVariant, {
  title: string;
  badge: string;
  subtitle: string;
  description: string;
  accent: string;
  basePath: string;
}> = {
  startup: {
    title: 'Startup workspace',
    badge: 'Startup dashboard',
    subtitle: 'Fast access to search, AI chat, regulations, charts, newsletters, and the global map.',
    description: 'A lightweight dashboard for fast-moving teams tracking policy changes and compliance signals.',
    accent: 'var(--ds-brand-blue)',
    basePath: '/startup/dashboard',
  },
  government: {
    title: 'Government workspace',
    badge: 'Government dashboard',
    subtitle: 'A focused view for regulatory monitoring, public-sector analysis, and policy discovery.',
    description: 'Built for agencies and public-sector teams that need quick visibility into regulatory change.',
    accent: 'var(--ds-info)',
    basePath: '/government/dashboard',
  },
};




export default function SegmentDashboardShell({ variant }: SegmentDashboardShellProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const config = DASHBOARD_CONFIG[variant];
  // The menu, the collapse state and the active entry all live in AppSidebar
  // now, so this shell keeps only what is its own: which variant it is and
  // what the main area shows.
  const isExactDashboard = useMatch(config.basePath);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={styles.layout}>
      {/* One sidebar for every shell (features/layout/AppSidebar). This page
          used to define its own twelve entries, which overlapped the main
          app's menu in three places and the dashboard's in six — the customer
          got a different product depending on which screen they were on. */}
      {/* This shell knows which variant it is; deriving the label from the plan
          instead gave a startup workspace an "Enterprise dashboard" badge
          whenever the account's organisation type said corporation. */}
      <AppSidebar
        badge={variant === 'startup' ? 'Startup dashboard' : 'Government dashboard'}
        footer={(
          <button className={styles.logoutBtn} onClick={handleLogout} title="Sign out">
            <svg className={styles.navIcon} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"/></svg>
            Sign out
          </button>
        )}
      />

      {/* Outside <main>, because `.main > *` stretches every direct child to
          width:100% — which stretched the dock's fixed-position wrapper across
          the viewport, so its `right: 16px` placed the bell at the far LEFT,
          on top of the sidebar. */}
      <NotificationDock />

      <main className={styles.main}>
        {isExactDashboard ? (
          <div style={{ padding: '1.5rem', display: 'grid', gap: 16, minWidth: 0 }}>
            {variant === 'government' && <PolicyDesk />}  {/* SRS #58 */}
            {/* The segment dashboards showed the coverage panel and nothing
                else — no figures, no charts — while /dashboard had all three.
                Same content, same order, whichever shell you land in. */}
            <AnalyticsPanel
              token={typeof window !== 'undefined' ? window.localStorage.getItem('policyora_token') : null}
              theme={typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'}
            />
            <OverviewPanel token={typeof window !== 'undefined' ? window.localStorage.getItem('policyora_token') : null} />
          </div>
        ) : (
          <Outlet context={{ selectedSessionId, setSelectedSessionId, refreshSessions: () => {} }} />
        )}
      </main>
    </div>
  );
}
